import {
  TopUpOptionsSchema,
  TopUpResultSchema,
  TopUpSchema,
  WalletEntrySchema,
} from "@uzverse/contracts";
import type { TopUp, TopUpOptions, WalletEntry } from "@uzverse/contracts";
import { useSyncExternalStore } from "react";
import { invokeWorldTool } from "../data.js";
import { navStore } from "../camera/nav.js";
import { vehicleHub } from "./hub.js";
import type { PoseMessage } from "./hub.js";
import type { CarState } from "./physics.js";
import { degToRad, parseModel, parseVehicle, radToDeg, wrapAngle } from "./types.js";
import type { VehicleEntity, VehicleModel } from "./types.js";
import type { DealerInfo } from "./showroom.js";

/**
 * Client state of the vehicle system: the catalog, the user's cars, the wallet, the Galeri panel
 * and which car is being driven. Server truth arrives through the world tools (the vehicles
 * backend); live poses of other drivers arrive as `vehicle.pose` messages and are interpolated by
 * the layer from `poses`. Per-frame values (the driven car's physics state) are plain fields: they
 * are read in `useFrame`, never rendered through React.
 */

export type NearTarget = { kind: "vehicle" | "dealer"; id: string; dist: number } | null;

/** The "Kredi yükle" dialog: options, the ledger, and a paid top-up that waits for its checkout. */
export interface TopUpUiState {
  open: boolean;
  tab: "buy" | "history";
  options: TopUpOptions | null;
  history: WalletEntry[];
  busy: boolean;
  /** i18n key suffix: `failed`, `topUpLimit`, `topUpUnavailable`. */
  error: string | null;
  /** A paid top-up whose checkout page is open; we poll until it completes. */
  pending: { id: string; url: string } | null;
  /** Credits added by the last completed top-up (the dialog and the toast show it). */
  lastAdded: number | null;
}

export interface VehiclesState {
  catalog: VehicleModel[];
  /** The dealership `vehicles.catalog` names (stock, placement). */
  dealership: DealerInfo | null;
  mine: VehicleEntity[];
  credits: number | null;
  /** The Galeri panel. */
  galeri: { open: boolean; dealerId: string | null; tab: "shop" | "mine" };
  topUp: TopUpUiState;
  /** The model and color shown in the panel (the displayed car takes the color live). */
  preview: { modelId: string | null; color: string | null };
  drivingId: string | null;
  near: NearTarget;
  busy: boolean;
  /** Last user-facing problem: an i18n key suffix (`insufficient`, `failed`). */
  error: string | null;
  /** The id of a car that just arrived in the bay (the reveal runs in the layer). */
  lastPurchase: string | null;
  /** Bumped when remote vehicle data changes in place. */
  rev: number;
}

export interface RemotePose {
  x: number;
  z: number;
  yaw: number;
  speed: number;
  /** performance.now() of arrival. */
  at: number;
}

const obj = (v: unknown): Record<string, unknown> =>
  typeof v === "object" && v !== null ? (v as Record<string, unknown>) : {};

const listOf = <T>(v: unknown, key: string, parse: (x: unknown) => T | null): T[] => {
  const raw = Array.isArray(v) ? v : obj(v)[key];
  return Array.isArray(raw) ? raw.map(parse).filter((x): x is T => x !== null) : [];
};

const creditsOf = (v: unknown): number | null => {
  const o = obj(v);
  const direct = o["credits"] ?? obj(o["wallet"])["credits"];
  return typeof direct === "number" && Number.isFinite(direct) ? direct : null;
};

function withoutDriver(v: VehicleEntity): VehicleEntity {
  const { driverUserId: _gone, ...rest } = v;
  return rest;
}

export class VehicleStore {
  private state: VehiclesState = {
    catalog: [],
    dealership: null,
    mine: [],
    credits: null,
    galeri: { open: false, dealerId: null, tab: "shop" },
    topUp: {
      open: false,
      tab: "buy",
      options: null,
      history: [],
      busy: false,
      error: null,
      pending: null,
      lastAdded: null,
    },
    preview: { modelId: null, color: null },
    drivingId: null,
    near: null,
    busy: false,
    error: null,
    lastPurchase: null,
    rev: 0,
  };
  private listeners = new Set<() => void>();

  /** The driven car's physics state (written by the drive rig every frame). */
  car: CarState | null = null;
  /** Speed in m/s (signed) and braking flag of the driven car, for HUD and lights. */
  speed = 0;
  braking = false;
  reversing = false;
  throttling = false;
  /** Latest pose of other drivers' vehicles by id (interpolated in the layer). */
  readonly poses = new Map<string, RemotePose>();
  /** Model ids the player may drive right now. */
  private pendingEnter: string | null = null;
  /** Cockpit camera instead of the chase camera. */
  cockpit = false;
  /** Engine sound toggle (off by default). */
  sound = false;
  /** The user id of the viewer (the session's user, set by the UI). */
  selfUserId = "";
  /** performance.now() when each car was revealed after a purchase. */
  readonly reveal = new Map<string, number>();

  constructor() {
    vehicleHub.onPose((m) => this.onRemotePose(m));
  }

  getState = (): VehiclesState => this.state;
  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  private set(patch: Partial<VehiclesState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  setSelf(userId: string): void {
    this.selfUserId = userId;
  }

  modelOf(id: string): VehicleModel | undefined {
    return this.state.catalog.find((m) => m.id === id);
  }

  vehicleOf(id: string): VehicleEntity | undefined {
    return this.state.mine.find((v) => v.id === id);
  }

  // ------------------------------------------------------------------ server reads

  async refresh(): Promise<void> {
    try {
      const [cat, mine, wallet] = await Promise.all([
        invokeWorldTool("vehicles.catalog", {}),
        invokeWorldTool("vehicles.mine", {}),
        invokeWorldTool("wallet.balance", {}),
      ]);
      const catalog = listOf(cat.output, "models", parseModel);
      const owned = listOf(mine.output, "vehicles", parseVehicle);
      // Keep the driven car's live pose; the server copy lags it.
      const d = obj(obj(cat.output)["dealership"]);
      const dealership: DealerInfo | null =
        typeof d["id"] === "string"
          ? {
              id: d["id"],
              name: typeof d["name"] === "string" ? d["name"] : "Galeri",
              placementId: typeof d["placementId"] === "string" ? d["placementId"] : "",
              stockModelIds: Array.isArray(d["stockModelIds"])
                ? d["stockModelIds"].filter((x): x is string => typeof x === "string")
                : [],
            }
          : this.state.dealership;
      this.set({
        dealership,
        catalog: catalog.length > 0 ? catalog : this.state.catalog,
        mine: owned,
        credits: creditsOf(wallet.output) ?? this.state.credits,
      });
    } catch {
      // The vehicle service is optional: no catalog, no galeri.
    }
  }

  // ------------------------------------------------------------------ wallet top-up

  /** How often a pending checkout is polled (tests shorten it). */
  pollMs = 2000;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  private setTopUp(patch: Partial<TopUpUiState>): void {
    this.set({ topUp: { ...this.state.topUp, ...patch } });
  }

  openTopUp(): void {
    this.setTopUp({ open: true, tab: "buy", error: null, lastAdded: null });
    void this.loadTopUp();
  }

  closeTopUp(): void {
    this.setTopUp({ open: false });
  }

  setTopUpTab(tab: "buy" | "history"): void {
    this.setTopUp({ tab });
    if (tab === "history") void this.loadHistory();
  }

  /** Options (provider, packages, limits) and the balance. */
  async loadTopUp(): Promise<void> {
    try {
      const [opts, wallet] = await Promise.all([
        invokeWorldTool("wallet.topUpOptions", {}),
        invokeWorldTool("wallet.balance", {}),
      ]);
      const options = TopUpOptionsSchema.safeParse(opts.output);
      const credits = creditsOf(wallet.output);
      this.set({
        ...(credits !== null ? { credits } : {}),
        topUp: { ...this.state.topUp, options: options.success ? options.data : null },
      });
    } catch {
      this.setTopUp({ options: null });
    }
  }

  async loadHistory(): Promise<void> {
    try {
      const out = await invokeWorldTool("wallet.history", { limit: 50 });
      const raw = obj(out.output)["entries"];
      const history = Array.isArray(raw)
        ? raw.flatMap((e) => {
            const p = WalletEntrySchema.safeParse(e);
            return p.success ? [p.data] : [];
          })
        : [];
      this.setTopUp({ history });
    } catch {
      // The ledger is a nicety: an empty list says nothing wrong.
    }
  }

  /**
   * Buys credits by package or a custom amount. A free provider answers `completed` at once; a
   * paid one answers `pending` with a checkout URL, which opens in a new tab while we poll.
   */
  async topUp(input: {
    packageId?: string;
    credits?: number;
  }): Promise<"completed" | "pending" | null> {
    if (this.state.topUp.busy) return null;
    this.setTopUp({ busy: true, error: null, lastAdded: null });
    try {
      const out = await invokeWorldTool("wallet.topUp", input);
      const parsed = TopUpResultSchema.safeParse(out.output);
      if (!parsed.success) throw new Error("bad top-up answer");
      const { topUp, wallet } = parsed.data;
      this.set({ credits: wallet.credits });
      if (topUp.status === "completed") {
        this.setTopUp({ busy: false, lastAdded: topUp.credits, pending: null });
        void this.loadHistory();
        return "completed";
      }
      if (topUp.status === "pending" && topUp.checkoutUrl) {
        this.setTopUp({ busy: false, pending: { id: topUp.id, url: topUp.checkoutUrl } });
        globalThis.open?.(topUp.checkoutUrl, "_blank", "noopener");
        this.pollTopUp(topUp.id);
        return "pending";
      }
      throw new Error("top-up failed");
    } catch (e) {
      const code = (e as { code?: string }).code;
      this.setTopUp({
        busy: false,
        error:
          code === "rate_limited"
            ? "topUpLimit"
            : code === "unavailable"
              ? "topUpUnavailable"
              : "failed",
      });
      return null;
    }
  }

  private pollTopUp(id: string): void {
    this.stopPolling();
    const startedAt = Date.now();
    this.pollTimer = setInterval(() => {
      void (async () => {
        try {
          const out = await invokeWorldTool("wallet.topUpStatus", { topUpId: id });
          const t: TopUp | null = (() => {
            const p = TopUpSchema.safeParse(out.output);
            return p.success ? p.data : null;
          })();
          if (!t || this.state.topUp.pending?.id !== id) return this.stopPolling();
          if (t.status === "completed") {
            this.stopPolling();
            this.setTopUp({ pending: null, lastAdded: t.credits });
            void this.loadTopUp();
            void this.loadHistory();
          } else if (t.status === "failed") {
            this.stopPolling();
            this.setTopUp({ pending: null, error: "failed" });
          }
        } catch {
          // A missed poll is retried on the next tick.
        }
        if (Date.now() - startedAt > 15 * 60_000) this.stopPolling();
      })();
    }, this.pollMs);
  }

  stopPolling(): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  /** Gives up waiting for a payment page (the top-up stays pending on the server). */
  cancelPending(): void {
    this.stopPolling();
    this.setTopUp({ pending: null });
  }

  // ------------------------------------------------------------------ panel

  openGaleri(dealerId: string | null, tab: "shop" | "mine" = "shop"): void {
    const first = this.state.catalog[0];
    const modelId = this.state.preview.modelId ?? first?.id ?? null;
    const model = modelId ? this.modelOf(modelId) : undefined;
    this.set({
      galeri: { open: true, dealerId, tab },
      preview: { modelId, color: this.state.preview.color ?? model?.colors[0] ?? null },
      error: null,
    });
    void this.refresh();
  }

  closeGaleri(): void {
    this.set({ galeri: { ...this.state.galeri, open: false } });
  }

  setTab(tab: "shop" | "mine"): void {
    this.set({ galeri: { ...this.state.galeri, tab } });
  }

  preview(modelId: string, color?: string): void {
    const model = this.modelOf(modelId);
    const keep =
      color ??
      (model?.colors.includes(this.state.preview.color ?? "") ? this.state.preview.color : null);
    this.set({ preview: { modelId, color: keep ?? model?.colors[0] ?? null } });
  }

  setPreviewColor(color: string): void {
    this.set({ preview: { ...this.state.preview, color } });
  }

  setNear(near: NearTarget): void {
    const a = this.state.near;
    if (a?.kind === near?.kind && a?.id === near?.id) return;
    this.set({ near });
  }

  // ------------------------------------------------------------------ actions

  private async run<T>(fn: () => Promise<T>): Promise<T | null> {
    if (this.state.busy) return null;
    this.set({ busy: true, error: null });
    try {
      return await fn();
    } catch (e) {
      const text = String((e as { message?: string }).message ?? e);
      const code = JSON.stringify((e as { details?: unknown }).details ?? "");
      this.set({
        error: /insufficient|credit|funds/i.test(text + code) ? "insufficient" : "failed",
      });
      return null;
    } finally {
      this.set({ busy: false });
    }
  }

  async buy(modelId: string, color: string): Promise<VehicleEntity | null> {
    const out = await this.run(() => invokeWorldTool("vehicles.buy", { modelId, color }));
    if (!out) return null;
    const vehicle = parseVehicle(obj(out.output)["vehicle"] ?? out.output);
    if (!vehicle) {
      this.set({ error: "failed" });
      return null;
    }
    this.reveal.set(vehicle.id, performance.now());
    const credits = creditsOf(out.output);
    this.set({
      mine: [...this.state.mine.filter((v) => v.id !== vehicle.id), vehicle],
      lastPurchase: vehicle.id,
      ...(credits !== null ? { credits } : {}),
    });
    if (credits === null) void this.refresh();
    return vehicle;
  }

  async sell(vehicleId: string): Promise<boolean> {
    const out = await this.run(() => invokeWorldTool("vehicles.sell", { vehicleId }));
    if (!out) return false;
    const credits = creditsOf(out.output);
    this.set({
      mine: this.state.mine.filter((v) => v.id !== vehicleId),
      ...(credits !== null ? { credits } : {}),
    });
    if (credits === null) void this.refresh();
    return true;
  }

  async paint(vehicleId: string, color: string): Promise<boolean> {
    const out = await this.run(() => invokeWorldTool("vehicles.paint", { vehicleId, color }));
    if (!out) return false;
    this.set({ mine: this.state.mine.map((v) => (v.id === vehicleId ? { ...v, color } : v)) });
    return true;
  }

  /** Flies the map camera to a car. */
  locate(vehicleId: string): void {
    const v = this.vehicleOf(vehicleId);
    if (!v) return;
    this.closeGaleri();
    if (this.state.drivingId) return;
    const mode = navStore.getState().mode;
    if (mode !== "orbit") navStore.setMode("orbit");
    navStore.requestFlight([v.pose.x, 1, v.pose.z], [v.pose.x + 14, 11, v.pose.z + 18]);
  }

  /** Gets in: the server marks the car as driving, the rig takes the camera. */
  async enter(vehicleId: string): Promise<boolean> {
    const v = this.vehicleOf(vehicleId);
    if (!v || this.state.drivingId) return false;
    const out = await this.run(() => invokeWorldTool("vehicles.enter", { vehicleId }));
    if (!out) return false;
    const server = parseVehicle(obj(out.output)["vehicle"]) ?? v;
    this.closeGaleri();
    this.car = null; // the rig seeds it from the pose
    this.speed = 0;
    this.pendingEnter = server.id;
    this.set({
      drivingId: server.id,
      mine: this.state.mine.map((x) => (x.id === server.id ? { ...server, state: "driving" } : x)),
      near: null,
    });
    // The rig renders in a live (continuous) mode.
    const mode = navStore.getState().mode;
    if (mode !== "first_person" && mode !== "third_person") navStore.setMode("third_person");
    return true;
  }

  /** Parks the car where it stands and goes back to walking next to it. */
  async exit(): Promise<void> {
    const id = this.state.drivingId;
    const car = this.car;
    if (!id) return;
    const pose = car
      ? { x: car.x, z: car.z, yawDeg: radToDeg(car.yaw) }
      : (this.vehicleOf(id)?.pose ?? { x: 0, z: 0, yawDeg: 0 });
    const odometerM = (this.vehicleOf(id)?.odometerM ?? 0) + (car?.distance ?? 0);
    this.set({
      drivingId: null,
      mine: this.state.mine.map((v) =>
        v.id === id ? withoutDriver({ ...v, state: "parked", pose, odometerM }) : v,
      ),
    });
    // The walker appears on the car's left, facing where the car pointed.
    if (car) {
      const side = 2.3;
      navStore.walker = {
        ...navStore.walker,
        x: car.x + Math.cos(car.yaw) * side,
        z: car.z - Math.sin(car.yaw) * side,
        y: 0,
        vy: 0,
        yaw: wrapAngle(car.yaw + Math.PI),
        pitch: 0,
        onGround: true,
      };
    }
    this.car = null;
    this.speed = 0;
    this.braking = false;
    try {
      const out = await invokeWorldTool("vehicles.exit", { vehicleId: id, pose });
      const server = parseVehicle(obj(out.output)["vehicle"]);
      if (server && this.state.drivingId === null) {
        this.set({ mine: this.state.mine.map((v) => (v.id === server.id ? server : v)) });
      }
    } catch {
      // The parked pose stays local; the next refresh reconciles it.
    }
  }

  /** The rig took the pending enter and seeded its state. */
  takePendingEnter(): VehicleEntity | undefined {
    const id = this.pendingEnter;
    this.pendingEnter = null;
    return id ? this.vehicleOf(id) : undefined;
  }

  // ------------------------------------------------------------------ realtime

  /** Sends the driven car's pose (the rig calls this at most ten times a second). */
  sendPose(): void {
    const id = this.state.drivingId;
    const car = this.car;
    if (!id || !car) return;
    vehicleHub.sendPose({
      vehicleId: id,
      x: car.x,
      z: car.z,
      yawDeg: radToDeg(car.yaw),
      speed: this.speed,
    });
  }

  private onRemotePose(m: PoseMessage): void {
    if (m.vehicleId === this.state.drivingId) return;
    this.poses.set(m.vehicleId, {
      x: m.x,
      z: m.z,
      yaw: degToRad(m.yawDeg),
      speed: m.speed,
      at: performance.now(),
    });
  }
}

export const vehicleStore = new VehicleStore();

export function useVehicles<T>(
  select: (s: VehiclesState) => T,
  store: VehicleStore = vehicleStore,
): T {
  return useSyncExternalStore(store.subscribe, () => select(store.getState()));
}
